"use client";

import {
  type KeyboardEvent as ReactKeyboardEvent,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { useRouter } from "next/navigation";

import MiniSearch from "minisearch";

import {
  type DocsSearchIndexDocument,
  normalizeDocsSearchIndexDocuments,
} from "@/lib/docs-index-normalize";
import type { DocsSearchScope } from "@/lib/docs-search-shared";
import { EventKeys } from "@/lib/observability/catalog";
import { EventPublisher } from "@/lib/observability/events";

const INDEX_URL = "/docs-search-index.json";

export const DOCS_SEARCH_MAX_RESULTS = 15;

export type DocsIndexDocument = DocsSearchIndexDocument;

export interface DocsSearchHit {
  href: string;
  id: string;
  title: string;
}

interface IndexPayload {
  documents: Partial<DocsSearchIndexDocument>[];
  generatedAt?: string;
}

async function fetchDocsSearchIndex(): Promise<DocsSearchIndexDocument[]> {
  const res = await fetch(INDEX_URL);

  if (!res.ok) {
    throw new Error(`HTTP ${res.status}`);
  }

  const data = (await res.json()) as IndexPayload;

  if (!Array.isArray(data.documents)) {
    return [];
  }

  return normalizeDocsSearchIndexDocuments(data.documents);
}

export function useDocsSearchModal() {
  const router = useRouter();
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [scope, setScope] = useState<DocsSearchScope>("all");
  const [loadError, setLoadError] = useState<string | null>(null);
  const [indexLoading, setIndexLoading] = useState(true);
  const [documents, setDocuments] = useState<DocsSearchIndexDocument[]>([]);
  const [selectedIndex, setSelectedIndex] = useState(0);
  const lastLoggedSignature = useRef<string>("");

  useEffect(() => {
    let cancelled = false;

    void (async () => {
      setIndexLoading(true);

      try {
        const docs = await fetchDocsSearchIndex();

        if (!cancelled) {
          setDocuments(docs);
          setLoadError(null);
        }
      } catch {
        if (!cancelled) {
          setLoadError("Search index missing. Run npm run docs:search-index.");
        }
      } finally {
        if (!cancelled) {
          setIndexLoading(false);
        }
      }
    })();

    return () => {
      cancelled = true;
    };
  }, []);

  const mini = useMemo(() => {
    if (documents.length === 0) return null;

    const m = new MiniSearch({
      fields: ["title", "description", "text"],
      idField: "id",
      storeFields: ["title", "href"],
    });

    m.addAll(
      documents.map((d) => ({
        description: d.description,
        href: d.href,
        id: d.id,
        text: d.text,
        title: d.title,
      })),
    );

    return m;
  }, [documents]);

  const results: DocsSearchHit[] = useMemo(() => {
    if (!mini || !query.trim()) return [];

    const raw = mini.search(query.trim(), {
      boost: { description: 2, text: 1, title: 3 },
      fuzzy: 0.2,
      prefix: true,
    });

    let rows = raw;

    if (scope !== "all") {
      rows = raw.filter((r) => {
        const doc = documents.find((d) => d.id === r.id);

        return doc?.kind === scope;
      });
    }

    return rows.slice(0, DOCS_SEARCH_MAX_RESULTS).map((r) => ({
      href: String(r.href),
      id: String(r.id),
      title: String(r.title),
    }));
  }, [documents, mini, query, scope]);

  const recentDocs = useMemo(() => {
    return [...documents]
      .sort((a, b) => (a.updatedAt < b.updatedAt ? 1 : -1))
      .slice(0, 8);
  }, [documents]);

  useEffect(() => {
    setSelectedIndex(0);
  }, [query, results.length, scope]);

  const goTo = useCallback(
    (href: string) => {
      setOpen(false);
      setQuery("");
      router.push(href);
    },
    [router],
  );

  useEffect(() => {
    function onKeyDown(e: KeyboardEvent) {
      if ((e.metaKey || e.ctrlKey) && e.key === "k") {
        e.preventDefault();
        setOpen(true);
      }
    }

    document.addEventListener("keydown", onKeyDown);

    return () => document.removeEventListener("keydown", onKeyDown);
  }, []);

  const onModalKeyDown = useCallback(
    (e: ReactKeyboardEvent) => {
      if (e.key === "ArrowDown") {
        e.preventDefault();
        setSelectedIndex((i) =>
          results.length === 0 ? 0 : Math.min(i + 1, results.length - 1),
        );
      } else if (e.key === "ArrowUp") {
        e.preventDefault();
        setSelectedIndex((i) =>
          results.length === 0 ? 0 : Math.max(i - 1, 0),
        );
      } else if (e.key === "Enter" && results[selectedIndex]) {
        e.preventDefault();
        goTo(results[selectedIndex].href);
      }
    },
    [goTo, results, selectedIndex],
  );

  useEffect(() => {
    const q = query.trim();

    if (q.length < 2 || loadError !== null) return;

    const signature = `${scope}|${q}|${results.length}`;
    const t = window.setTimeout(() => {
      if (signature === lastLoggedSignature.current) return;
      lastLoggedSignature.current = signature;
      void EventPublisher.publishEvent(EventKeys.DOCS_SEARCH_PERFORMED, {
        query_word_count: q.split(/\s+/).filter(Boolean).length,
        result_count: results.length,
        scope,
      });
    }, 600);

    return () => window.clearTimeout(t);
  }, [loadError, query, results.length, scope]);

  const canSearch = mini !== null && loadError === null && !indexLoading;

  return {
    canSearch,
    documents,
    goTo,
    indexLoading,
    loadError,
    onModalKeyDown,
    open,
    query,
    recentDocs,
    results,
    scope,
    selectedIndex,
    setOpen,
    setQuery,
    setScope,
    setSelectedIndex,
  };
}
